import React, { useMemo, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import debounce from "lodash/debounce";
import { useHistory } from "react-router-dom";
import { EntryRegistrationFormValues } from "./types";
import "./EntryRegistrationForm.css";
import VisitorDataForm from "./VisitorDataForm/VisitorDataForm";
import AccessTypeForm from "./AccessTypeForm/AccessTypeForm";
import ObservationsForm from "./ObservationsForm/ObservationsForm";
import AutorizacionesForm from "./AutorizacionesForm/AutorizacionesForm";
import { useTransportAuthorizationFlow } from "./AccessTypeForm/TransportContentForm/useTransportAuthorizationFlow";
import Button from "../../../../components/common/PL/Button/Button";
import AccessVehicleForm from "./AccessVehicleForm/AccessVehicleForm";
import AccessRulesForm from "./AccessRulesForm/AccessRulesForm";
import Toast from "../../../../components/common/PL/Toast/Toast";
import ScanLoadingOverlay from "../../../../components/common/PL/ScanLoadingOverlay/ScanLoadingOverlay";
import DialogConfirmation from "../../../../components/common/PL/Dialog/DialogConfirmation";
import BlacklistDialog from "../../../../components/common/PL/Dialog/BlacklistDialog";
import { RegistrationMode } from "../registroIngresoNavigation";
import { useRegistroIngresoSession } from "../session/RegistroIngresoSessionContext";
import { RoutePaths } from "../../../../utils/Items";
import useEvent from "../hooks/useEvent";
import { areSameCalendarDate, parseBirthDateValue } from "../utils/visitorPersonalData";
import { flattenErrors } from "./fieldLabelMap";
import MissingFieldsSummary from "./MissingFieldsSummary/MissingFieldsSummary";
import FloatingFieldNavigator from "./FloatingFieldNavigator/FloatingFieldNavigator";

type EntryRegistrationFormProps = {
  tabId: string;
  mode: RegistrationMode;
  values: EntryRegistrationFormValues;
  onValuesChange: (tabId: string, values: EntryRegistrationFormValues) => void;
  vehicles?: any[];
  authorizations?: any[];
  isSearching?: boolean;
};

/** El flujo de autorizaciones de transporte (elegir empresa, listar/solicitar autorizaciones) se
 * comparte entre el paso "Tipo de autorización" y el paso "Autorizaciones": por eso se instancia
 * una única vez acá, y no dentro de cada paso, para que ambos lean y escriban el mismo estado en
 * vez de tener cada uno el suyo. Vive en un componente aparte (y no directo en
 * `EntryRegistrationForm`) para que sólo se monte —y sólo dispare sus fetchs— cuando el método de
 * acceso elegido es "transport", igual que antes cuando esta lógica vivía toda dentro de
 * `TransportContentForm`. */
type TransportStepsProps = {
  formId: string;
  mode: RegistrationMode;
  authorizations?: any[];
  isSearching?: boolean;
};

const TransportSteps = ({ formId, mode, authorizations, isSearching }: TransportStepsProps) => {
  const transportFlow = useTransportAuthorizationFlow(mode);

  return (
    <>
      <AccessTypeForm
        formId={formId}
        mode={mode}
        authorizations={authorizations}
        transportFlow={transportFlow}
      />
      <AccessRulesForm formId={formId} mode={mode} isTransport />
      <ObservationsForm formId={formId} isTransport mode={mode} isSearching={isSearching} />
      <AutorizacionesForm formId={formId} flow={transportFlow} />
    </>
  );
};

const EntryRegistrationForm = ({
  tabId,
  mode,
  values,
  onValuesChange,
  vehicles,
  authorizations,
  isSearching,
}: EntryRegistrationFormProps) => {
  const { sendEvent, loadingSubmit } = useEvent();
  const history = useHistory();
  const { tabs, closeTab, clearSession } = useRegistroIngresoSession();

  const methods = useForm<EntryRegistrationFormValues>({
    mode: "onBlur",
    defaultValues: values,
  });

  const { errors, isSubmitted } = methods.formState;

  const flattenedErrors = isSubmitted ? flattenErrors(errors) : [];

  // Todas las pestañas abiertas se renderizan a la vez y sólo la inactiva se tapa con
  // `display: none` (ver RegistroIngreso.tsx), así que los `data-field-name` están repetidos una vez
  // por pestaña. El resumen y el botón flotante buscan el campo faltante dentro de este formulario
  // para no terminar apuntando al de otra pestaña, donde el scroll y el foco no hacen nada.
  const formRef = React.useRef<HTMLFormElement>(null);

  const debouncedUpdateValues = useMemo(
    () =>
      debounce((nextValues: EntryRegistrationFormValues) => {
        onValuesChange(tabId, nextValues);
      }, 250),
    [onValuesChange, tabId]
  );

  React.useEffect(() => {
    const subscription = methods.watch((nextValues) => {
      debouncedUpdateValues(nextValues as EntryRegistrationFormValues);
    });

    return () => {
      subscription.unsubscribe();
      debouncedUpdateValues.cancel();
      onValuesChange(tabId, methods.getValues());
    };
  }, [methods, debouncedUpdateValues, onValuesChange, tabId]);

  // Update form values when external search finishes
  React.useEffect(() => {
    const currentValues = methods.getValues();
    const hasNewEventId = values.eventId && values.eventId !== currentValues.eventId;
    const hasNewId = values.id && values.id !== currentValues.id;
    const hasNewName =
      (values.firstName && values.firstName !== currentValues.firstName) ||
      (values.lastName && values.lastName !== currentValues.lastName);
    const hasNewAccessMethod =
      values.selectedAccessMethod &&
      values.selectedAccessMethod !== currentValues.selectedAccessMethod;
    const hasNewBirthDate = !areSameCalendarDate(
      parseBirthDateValue(values.birthdate),
      parseBirthDateValue(currentValues.birthdate)
    );
    const hasNewSex = values.sex !== currentValues.sex;
    // En una salida por vehículo, la ficha del asociado puede completarse después de que el
    // formulario ya montó: primero llega la patente desde `getVehiclesInside` y luego la ficha
    // completa desde `getVehicles`. Sin esta comparación `useForm` conserva el primer snapshot y
    // el acoplado queda mostrando sólo la patente.
    const hasNewPreselectedTrailerData =
      mode === "exit" &&
      values.exitUnitsPreselected &&
      JSON.stringify(values.trailerVehicles || []) !==
        JSON.stringify(currentValues.trailerVehicles || []);
    if (
      hasNewEventId ||
      hasNewId ||
      hasNewName ||
      hasNewAccessMethod ||
      hasNewBirthDate ||
      hasNewSex ||
      hasNewPreselectedTrailerData
    ) {
      // `keepFieldsRef` por lo mismo que en el formulario de vehículo: los campos que se registran
      // a mano desde un efecto (`selectedVehicleId`, `transportAuthorizationSelected`) no se vuelven
      // a registrar solos, y sin sus reglas el formulario se podría enviar sin autorización.
      methods.reset(values, { keepFieldsRef: true });
    }
  }, [
    values.eventId,
    values.id,
    values.firstName,
    values.lastName,
    values.selectedAccessMethod,
    values.birthdate,
    values.sex,
    values.exitUnitsPreselected,
    values.trailerVehicles,
    mode,
    values,
    methods,
  ]);

  const [toast, setToast] = useState({
    open: false,
    message: "",
    severity: "success" as "success" | "error" | "warning" | "info",
  });

  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [blacklistDialogOpen, setBlacklistDialogOpen] = useState(false);

  React.useEffect(() => {
    if (mode !== "exit" && values.isBlackList && !values.blacklistAuthorization) {
      setBlacklistDialogOpen(true);
    }
  }, [mode, values.isBlackList, values.blacklistedData, values.blacklistAuthorization]);

  const hasEntrance = Boolean(values.entranceEvent);
  const eventType = mode === "exit" ? "out" : "in";

  const onSubmit = methods.handleSubmit(
    async (data) => {
      // Egreso con ingreso previo sigue conociendo su vehículo de antemano (getVehicles/entrance
      // event, ver RegistroIngresoSessionContext) y depende de `vehicles`. Sin ingreso previo
      // ("Registrar salida sin ingreso previo") pasa por el mismo buscador que el ingreso: lo que
      // hay en `data.associatedVehicles` es lo que fue devolviendo `selectVehicle` a medida que el
      // guardia elegía filas del buscador, y es eso lo que hay que mandar, no el listado crudo.
      const { success, error } = await sendEvent(
        {
          ...data,
          associatedVehicles: mode === "exit" && hasEntrance ? vehicles : data.associatedVehicles,
        },
        eventType,
        authorizations
      );
      if (success) {
        setToast({
          open: true,
          message: "Formulario enviado correctamente.",
          severity: "success",
        });

        if (tabs.length > 1) {
          closeTab(tabId);
        } else {
          clearSession();
          history.push(RoutePaths.LogisticParkHome);
        }
      } else {
        setToast({
          open: true,
          message: error || "Hubo un error al enviar el formulario.",
          severity: "error",
        });
      }
    },
    (errors) => {
      if (Object.keys(errors).length > 0) {
        setToast({
          open: true,
          message: "Hay campos faltantes. Por favor completa los campos marcados.",
          severity: "error",
        });
      }
    }
  );

  const selectedAccessMethod = methods.watch("selectedAccessMethod");
  const isTransport = selectedAccessMethod === "transport";
  const submitLabel = mode === "exit" ? "Registrar salida" : "Registrar Ingreso";

  const onCancel = () => {
    setShowCancelConfirm(true);
  };

  const handleConfirmCancel = async () => {
    setShowCancelConfirm(false);
    await sendEvent(methods.getValues(), "reject", authorizations);

    if (tabs.length > 1) {
      closeTab(tabId);
    } else {
      clearSession();
      history.push(RoutePaths.LogisticParkHome);
    }
  };

  const handleBlacklistReject = () => {
    setBlacklistDialogOpen(false);
    if (tabs.length > 1) {
      closeTab(tabId);
    } else {
      clearSession();
      history.push(RoutePaths.LogisticParkHome);
    }
  };

  const handleBlacklistAllow = (authorizedBy: string) => {
    methods.setValue("blacklistAuthorization", authorizedBy, { shouldDirty: true });
    setBlacklistDialogOpen(false);
  };

  return (
    <FormProvider {...methods}>
      <form className="entryRegistrationForm" onSubmit={onSubmit} ref={formRef}>
        <VisitorDataForm
          formId={tabId}
          disabled={mode === "exit" && hasEntrance}
          authorizations={authorizations}
        />
        {isTransport ? (
          <TransportSteps
            formId={tabId}
            mode={mode}
            authorizations={authorizations}
            isSearching={isSearching}
          />
        ) : (
          <>
            <AccessTypeForm formId={tabId} mode={mode} authorizations={authorizations} />
            <AccessVehicleForm formId={tabId} mode={mode} vehicles={vehicles} />
            <AccessRulesForm formId={tabId} mode={mode} isTransport={false} />
            <ObservationsForm
              formId={tabId}
              isTransport={false}
              mode={mode}
              isSearching={isSearching}
            />
          </>
        )}
        <MissingFieldsSummary errors={flattenedErrors} scopeRef={formRef} />
        <div className="entryRegistrationButtons">
          <Button
            variant="outline"
            size="large"
            type="button"
            onClick={onCancel}
            disabled={loadingSubmit}
          >
            Cancelar
          </Button>
          <Button
            loading={loadingSubmit}
            size="large"
            type="submit"
            variant="success"
            disabled={loadingSubmit}
          >
            {loadingSubmit ? "Enviando..." : submitLabel}
          </Button>
        </div>
      </form>
      <DialogConfirmation
        open={showCancelConfirm}
        onClose={() => setShowCancelConfirm(false)}
        onConfirm={handleConfirmCancel}
        title="Cerrar Formulario"
        message="¿Está seguro que desea cerrar esta pestaña? Se perderán todos los datos ingresados."
        confirmLabel="Aceptar"
        cancelLabel="Cancelar"
      />
      <BlacklistDialog
        open={blacklistDialogOpen}
        blacklistedData={values.blacklistedData}
        onReject={handleBlacklistReject}
        onAllow={handleBlacklistAllow}
      />
      <Toast
        open={toast.open}
        message={toast.message}
        severity={toast.severity}
        onClose={() => setToast((prev) => ({ ...prev, open: false }))}
      />
      <ScanLoadingOverlay
        open={loadingSubmit}
        message={mode === "exit" ? "Registrando salida..." : "Registrando ingreso..."}
      />
      <FloatingFieldNavigator errors={flattenedErrors} scopeRef={formRef} />
    </FormProvider>
  );
};

export default EntryRegistrationForm;
